import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useSetMultiStake } from "@/features/betslip/hooks/useStakeActions";
import { useRemainingPromoBalance } from "@/features/betslip/hooks/useRemainingPromoBalance";
import type { BetSelection } from "@/features/betslip/types";
import { BetCard } from "@/features/betslip/components/cards/BetCard";
import { BetSpine } from "@/features/betslip/components/parts/BetSpine";
import { EventGroup } from "@/features/betslip/components/multi/EventGroup";
import { StakeInput } from "@/features/betslip/components/parts/StakeInput";
import { MultiAlert } from "@/features/betslip/components/parts/MultiAlert";
import { InlineAlert } from "@/features/betslip/components/parts/InlineAlert";
import { OddsDisplay } from "@/features/betslip/components/parts/OddsDisplay";
import { PayoutValue } from "@/features/betslip/components/parts/PayoutValue";
import { StakeStrip } from "@/features/betslip/components/parts/StakeStrip";
import { QuickStakeChips } from "@/features/betslip/components/parts/QuickStakeChips";
import { PromoPills } from "@/features/betslip/components/parts/tokens/PromoPills";

export const MultiCard = () => {
  const { t } = useTranslation();
  const multiCardVisible = useBetslipStore((s) => s.multiCardVisible);
  const canFormMulti = useBetslipStore((s) => s.canFormMulti);
  const multiableSelections = useBetslipStore((s) => s.multiableSelections);
  const multiOdds = useBetslipStore((s) => s.multiOdds);
  const multiNewOdds = useBetslipStore((s) => s.multiNewOdds);
  const multiPayout = useBetslipStore((s) => s.multiPayout);
  // Once placed, show the price the server took.
  const acceptedOdds = useBetslipStore((s) =>
    s.multiResult?.status === "accepted" ? s.multiResult.odds : undefined,
  );
  const multiHasSP = useBetslipStore((s) => s.multiHasSP);
  const multiHasLiveViewOnlyLeg = useBetslipStore(
    (s) => s.multiHasLiveViewOnlyLeg,
  );
  const multiStake = useBetslipStore((s) => s.multiDisplayStake);
  const isStakeDisabled = useBetslipStore((s) => s.multiStakeDisabled);
  const isUsingPromo = useBetslipStore((s) => s.promoCash.multi);
  const setMultiStake = useSetMultiStake();
  const maxPromoStakeCents = useRemainingPromoBalance({ type: "multi" });
  const [hasStakeFocus, setHasStakeFocus] = useState(false);

  const groups = useMemo(() => {
    const map = new Map<string, BetSelection[]>();
    for (const sel of multiableSelections) {
      const existing = map.get(sel.eventId);
      if (existing) existing.push(sel);
      else map.set(sel.eventId, [sel]);
    }
    return [...map.entries()];
  }, [multiableSelections]);

  if (!multiCardVisible) return null;

  return (
    <BetCard testId="multi-card" alert={<MultiAlert />}>
      {!canFormMulti && (
        <InlineAlert
          alert={{
            variant: "warning",
            message: t("Make 2+ valid selections to submit a Multi"),
          }}
        />
      )}
      <BetSpine>
        {groups.map(([eventId, bets]) => (
          <EventGroup key={eventId} eventId={eventId} bets={bets} />
        ))}
      </BetSpine>
      <StakeStrip
        testId="multi-stake-strip"
        promoActive={isUsingPromo}
        leftSlot={
          <StakeInput
            value={multiStake}
            onChange={(cents) => setMultiStake(cents)}
            disabled={isStakeDisabled}
            testId="multi-stake-input"
            onFocus={() => setHasStakeFocus(true)}
            onBlur={() => setHasStakeFocus(false)}
          />
        }
        middleSlot={
          // Previews the pending product with a held indicator while any leg
          // has an acceptance pending; it eases out once accepted.
          <OddsDisplay
            odds={acceptedOdds ?? multiOdds}
            newOdds={acceptedOdds ? undefined : multiNewOdds}
            dash={multiHasSP || multiHasLiveViewOnlyLeg}
            showPreviousOdds
          />
        }
        rightSlot={
          <PayoutValue
            dollars={multiPayout}
            tbd={multiHasSP}
            testId="multi-payout"
          />
        }
        quickStake={
          <QuickStakeChips
            isOpen={hasStakeFocus && !isStakeDisabled}
            valueCents={multiStake}
            onSetCents={(cents) => setMultiStake(cents)}
            isPromo={isUsingPromo}
            maxPromoStakeCents={maxPromoStakeCents}
            arrowOffset="calc(-50%)"
            detached
          />
        }
      />
      <PromoPills
        outcomeIds={multiableSelections.map((s) => s.outcomeId)}
        isMulti
        selection={multiableSelections[0]}
      />
    </BetCard>
  );
};
